import React from 'react';

import { useRouter } from 'next/router';

import { useMediaQuery, useTheme } from '@mui/material';

import {
  FindListingsResponse,
  FindMapDataResponse,
  FindMapListingResponse,
  Listing,
} from '@kamernet/core/ApiClient/Kamernet';
import { useIntl } from '@kamernet/core/Intl';
import { ROUTES } from '@kamernet/core/Routing/routes';

import { transformMapListingsToListings } from '@kamernet/modules/Listing/utilities';
import {
  ListingsSearchAllFilters,
  ListingsSearchBarFilters,
  ListingsSearchSideFilters,
} from '@kamernet/modules/Search/types';
import {
  getEmptyListingsSearchAllFilters,
  getEmptyListingsSearchSideFilters,
  getListingsSearchSideFiltersCount,
  isStreetSearch,
  mapListingsSearchAllFiltersToBarFilters,
  mapListingsSearchAllFiltersToSideFilters,
} from '@kamernet/modules/Search/utilities';

import {
  IListingsSearch,
  ListingsSearchContext,
} from './ListingsSearchContext';
import { mapAllFiltersToRouteParams } from './utilities';

export const ListingsSearchProvider = ({
  children,
  findListingsResponse,
  findMapDataResponse,
  findMapListingsResponse,
  allFilters,
  activeTenantsCount,
}: {
  children: React.ReactNode;
  findListingsResponse: FindListingsResponse | null;
  findMapDataResponse: FindMapDataResponse | null;
  findMapListingsResponse: FindMapListingResponse | null;
  allFilters: ListingsSearchAllFilters;
  activeTenantsCount: number;
}) => {
  const { locale } = useIntl();
  const router = useRouter();
  const theme = useTheme();
  const isMDUp = useMediaQuery(theme.breakpoints.up('md'));

  const [isSideFiltersFormOpen, setIsSideFiltersFormOpen] =
    React.useState(false);

  const updateURL = React.useCallback(
    (newAllFilters: ListingsSearchAllFilters) => {
      const route = isStreetSearch(newAllFilters)
        ? ROUTES.ListingsSearchStreet
        : ROUTES.ListingsSearchCity;

      router
        .push(route.getURL(mapAllFiltersToRouteParams(newAllFilters), locale))
        .catch(() => {
          // ignore
        });
    },
    [locale, router],
  );

  const applyNewAllFilters = React.useCallback(
    (newAllFilters: Partial<ListingsSearchAllFilters>) => {
      setIsSideFiltersFormOpen(false);

      const isCityChanged = newAllFilters.citySlug !== allFilters.citySlug;

      updateURL({
        ...allFilters,
        pageNo: 1,
        mapMarkerLat: 0,
        mapMarkerLng: 0,
        ...newAllFilters,
        ...(isCityChanged && { streetName: null, streetSlug: null }),
      });
    },
    [allFilters, updateURL],
  );

  const value = React.useMemo<IListingsSearch>(() => {
    const sideFilters = mapListingsSearchAllFiltersToSideFilters(allFilters);
    let listings: Listing[] = [];
    let topListings: Listing[] = [];
    let nearbyListings: Listing[] = [];
    let totalListingsCount = 0;

    if (findListingsResponse) {
      listings = findListingsResponse.listings || [];
      topListings = findListingsResponse.topAdListings || [];
      nearbyListings = findListingsResponse.nearbyListings || [];
      totalListingsCount = findListingsResponse.total;
    } else if (findMapListingsResponse) {
      listings = transformMapListingsToListings(
        findMapListingsResponse.listings,
      );

      totalListingsCount = listings.length;
    }

    return {
      allFilters,
      sideFilters,
      barFilters: mapListingsSearchAllFiltersToBarFilters(allFilters),
      listings,
      totalListingsCount,
      topListings,
      nearbyListings,
      sideFiltersCount: getListingsSearchSideFiltersCount(sideFilters, {
        isMDUp,
      }),
      applyNewAllFilters,
      reload: () => {
        updateURL({ ...allFilters });
      },
      resetAllFilters: () => {
        updateURL(getEmptyListingsSearchAllFilters());
      },
      isSideFiltersFormOpen,
      toggleSideFiltersForm: () => {
        setIsSideFiltersFormOpen(p => !p);
      },
      applyNewSideFilters: (newSideFilters: ListingsSearchSideFilters) => {
        applyNewAllFilters(newSideFilters);
      },
      applyNewBarFilters: (newBarFilters: ListingsSearchBarFilters) => {
        applyNewAllFilters(newBarFilters);
      },
      resetSideFilters: () => {
        applyNewAllFilters(getEmptyListingsSearchSideFilters());
      },
      pinListings: findMapDataResponse?.mapdata || [],
      activeTenantsCount,
    };
  }, [
    allFilters,
    findListingsResponse,
    findMapListingsResponse,
    isMDUp,
    applyNewAllFilters,
    isSideFiltersFormOpen,
    findMapDataResponse?.mapdata,
    activeTenantsCount,
    updateURL,
  ]);

  return (
    <ListingsSearchContext.Provider value={value}>
      {children}
    </ListingsSearchContext.Provider>
  );
};
